import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, Scroll } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useContent } from "../../context/ContentContext";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const field = "rounded-none border-navy/20 bg-white focus-visible:ring-2 focus-visible:ring-gold focus-visible:border-gold";

function PolicyCard({ p, onSaved, onDeleted }) {
  const [draft, setDraft] = useState(p);
  const [saving, setSaving] = useState(false);
  const changed = draft.category !== p.category || draft.title !== p.title || draft.body !== p.body;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/policies/${p.id}`, { category: draft.category, title: draft.title, body: draft.body });
      toast.success("Policy saved"); onSaved();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete policy "${p.title}"?`)) return;
    try { await api.delete(`/admin/policies/${p.id}`); toast.success("Policy deleted"); onDeleted(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="bg-white border border-navy/10 p-5" data-testid={`admin-policy-${p.id}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-navy/60 mb-1 block">Category</label>
          <Input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className={`${field} h-10`} data-testid={`policy-category-${p.id}`} />
        </div>
        <div>
          <label className="text-xs font-medium text-navy/60 mb-1 block">Title</label>
          <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={`${field} h-10`} data-testid={`policy-title-${p.id}`} />
        </div>
      </div>
      <label className="text-xs font-medium text-navy/60 mb-1 block mt-3">Body</label>
      <Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={4} className={field} data-testid={`policy-body-${p.id}`} />
      <div className="flex gap-3 mt-3">
        <button onClick={save} disabled={!changed || saving} data-testid={`policy-save-${p.id}`} className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-sm hover:bg-gold hover:text-navy transition-colors disabled:opacity-40">
          <FloppyDisk size={16} /> {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={del} data-testid={`policy-delete-${p.id}`} className="inline-flex items-center gap-2 border border-navy/20 text-navy text-sm font-semibold px-4 py-2 rounded-sm hover:border-red-500 hover:text-red-500 transition-colors">
          <Trash size={16} /> Delete
        </button>
      </div>
    </div>
  );
}

export const PoliciesPanel = () => {
  const { content, reload } = useContent();
  const [policies, setPolicies] = useState([]);
  const [intro, setIntro] = useState("");
  const [savingIntro, setSavingIntro] = useState(false);
  const [form, setForm] = useState({ category: "", title: "", body: "" });

  const load = () => api.get("/policies").then((r) => {
    setIntro(r.data.intro || "");
    const flat = r.data.groups.flatMap((g) => g.policies);
    setPolicies(flat);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const categories = [...new Set(policies.map((p) => p.category))];

  const saveIntro = async () => {
    setSavingIntro(true);
    try {
      const { data } = await api.post("/admin/save", { content: { policies_intro: intro }, description: "Updated policies intro" });
      if (data.pending) toast.success("Submitted for master approval");
      else { await reload(); toast.success("Intro saved"); }
    } catch (e) { toast.error("Save failed"); } finally { setSavingIntro(false); }
  };

  const add = async () => {
    if (!form.category || !form.title || !form.body) { toast.error("Category, title and body are required"); return; }
    try { await api.post("/admin/policies", form); setForm({ category: "", title: "", body: "" }); load(); toast.success("Policy added"); }
    catch (e) { toast.error("Failed to add"); }
  };

  return (
    <div className="space-y-8" data-testid="policies-panel">
      <div className="bg-white border border-navy/10 p-6">
        <h3 className="font-heading text-xl text-navy mb-3 flex items-center gap-2"><Scroll size={22} /> Intro text</h3>
        <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} className={field} data-testid="policies-intro" />
        <button onClick={saveIntro} disabled={savingIntro || !intro.trim() || intro === (content.policies_intro || "")} data-testid="save-intro-btn" className="mt-3 inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-sm hover:bg-gold hover:text-navy transition-colors disabled:opacity-40">
          <FloppyDisk size={16} /> {savingIntro ? "Saving…" : "Save intro"}
        </button>
      </div>

      <div className="bg-white border border-navy/10 p-6">
        <h3 className="font-heading text-xl text-navy mb-4 flex items-center gap-2"><Plus size={22} /> Add a policy</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input list="policy-categories" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${field} h-10`} data-testid="new-policy-category" />
          <datalist id="policy-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${field} h-10`} data-testid="new-policy-title" />
        </div>
        <Textarea placeholder="Policy text" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${field} mt-4`} data-testid="new-policy-body" />
        <button onClick={add} data-testid="add-policy-btn" className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-gold hover:text-navy transition-colors">
          <Plus size={18} /> Add policy
        </button>
      </div>

      <div className="space-y-4">
        {policies.map((p) => (
          <PolicyCard key={p.id} p={p} onSaved={load} onDeleted={load} />
        ))}
      </div>
    </div>
  );
};
